/**
 * EmailMessageBody — renders an email message's body inside a
 * sandboxed iframe when HTML is available, falling back to a plain
 * `<p>` for plain-text messages. The iframe auto-resizes to its
 * content height so the message bubble grows naturally; the
 * `sandbox` attribute blocks scripts/forms/popups so untrusted
 * external sender HTML can't run anything in our app context.
 *
 * Why an iframe (vs DOMPurify + dangerouslySetInnerHTML)?
 * Email HTML carries inline `<style>` rules, table layouts, and
 * absolute font sizes that bleed into our CSS. The iframe gives us
 * a clean style boundary AND a security sandbox, with zero new
 * dependencies.
 */
import React, { useEffect, useRef, useState } from 'react';

const EmailMessageBody = ({ html, text, darkMode }) => {
  const iframeRef = useRef(null);
  // Start tall enough that the signature card (~150px on its own)
  // is visible while the iframe is reporting its real height. The
  // resize message will shrink/grow it to the exact size shortly
  // after mount.
  const [height, setHeight] = useState(260);
  // Stable per-instance id so multiple iframes on the page only
  // react to their own postMessage. Persists across re-renders.
  const idRef = useRef(`emb-${Math.random().toString(36).slice(2, 9)}`);

  // iter124 — Live-detect dark mode at render time so inbound email
  // text inherits white instead of black inside the iframe. Caller
  // can also pass `darkMode` explicitly to override.
  const isDark = typeof darkMode === 'boolean'
    ? darkMode
    : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  // Listen for height messages from THIS iframe only. Hook is at top
  // level so it's never called conditionally regardless of which
  // branch we render.
  useEffect(() => {
    if (!html) return undefined;
    const myId = idRef.current;
    const handler = (e) => {
      if (!e?.data || !e.data.__emailBody) return;
      if (e.data.id !== myId) return;
      const h = Number(e.data.h) || 60;
      // Grow-only: take the LARGER of current vs reported height.
      // Email signatures load images late; we don't want the iframe
      // to flicker shorter while the image is still being fetched.
      setHeight((prev) => Math.min(Math.max(h + 6, prev), 4000));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [html]);

  // Plain-text path — no HTML, just render the text. Preserve
  // whitespace + wrap long lines.
  if (!html) {
    return (
      <p className="text-sm whitespace-pre-wrap break-words text-foreground dark:text-white" data-testid="email-message-body-text">
        {text || ''}
      </p>
    );
  }

  // iter124 — Inject dark-mode CSS into the iframe shell so HTML
  // emails are readable in dark mode. We force common text-color
  // resets (`color`, `* { color: inherit !important }`) which
  // overrides hardcoded black text in marketing-style email
  // signatures. Background stays transparent so the parent bubble
  // color shows through.
  const textColor = isDark ? '#fff' : '#000';
  const linkColor = isDark ? '#aebfa6' : '#586D52';
  const quoteBorder = isDark ? '#3a3a3a' : '#d0d0d0';
  const quoteText = isDark ? '#aaa' : '#666';

  // Wrap raw HTML in a tiny shell that gives it a sane base font and
  // forces images to scale. `target=_blank` on every link so they
  // open outside the sandboxed frame.
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>
    html,body{margin:0;padding:0;background:transparent;color:${textColor};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:14px;line-height:1.55;word-wrap:break-word;overflow-wrap:anywhere}
    body{padding:2px 0;color:${textColor}}
    body *{color:${textColor} !important}
    body a{color:${linkColor} !important}
    img{max-width:100%;height:auto}
    table{max-width:100%}
    blockquote{margin:6px 0 6px 12px;padding-left:10px;border-left:3px solid ${quoteBorder};color:${quoteText} !important}
    pre{white-space:pre-wrap}
  </style></head><body>${html}<script>(function(){
    var __id=${JSON.stringify(idRef.current)};
    function reportHeight(){
      var h=Math.max(document.body.scrollHeight,document.documentElement.scrollHeight);
      parent.postMessage({__emailBody:true,id:__id,h:h},'*');
    }
    window.addEventListener('load',reportHeight);
    setTimeout(reportHeight,80);
    setTimeout(reportHeight,400);
    setTimeout(reportHeight,1200);
    if(typeof ResizeObserver!=='undefined'){
      new ResizeObserver(reportHeight).observe(document.documentElement);
    }
    var imgs=document.images||[];
    for(var i=0;i<imgs.length;i++){imgs[i].addEventListener('load',reportHeight);imgs[i].addEventListener('error',reportHeight);}  
  })();</script></body></html>`;

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      srcDoc={srcDoc}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', height, border: 0, background: 'transparent', display: 'block' }}
      data-testid="email-message-body-html"
    />
  );
};

export default EmailMessageBody;
